import { DISABLED, SELECTED, PLAYER1_COLOR, PLAYER2_COLOR, JUNIOR, SENIOR, GRADUATE } from './const';
import { PLAYER1, PLAYER2, DRAW, BEGINNER, HUMAN_VS_ROBOT } from './const';
import _ from 'lodash';
import Player2Icon from './ASSETS/player2.png';
import BeginnerIcon from './ASSETS/skill.beg.png';
import JuniorIcon from './ASSETS/skill.jun.png';
import SeniorIcon from './ASSETS/skill.sen.png';
import GraduateIcon from './ASSETS/skill.grad.png';
import { windowSize, delay, isNum, dbget, dbset } from '../HOME/utils';
import { HIDDEN, SLOW } from '../HOME/const';

export function sameLine(i1, i2, j1, j2) {
  return (i1 === j1 && i2 === j2) || (i1 === j2 && i2 === j1);
}

function touch(i1, i2, j1, j2) {
  if (sameLine(i1, i2, j1, j2)) {
    return undefined;
  }

  if (i1 === j1 || i1 === j2) {
    return i1;
  }

  if (i2 === j1 || i2 === j2) {
    return i2;
  }

  return undefined;
}

function cross(i1, i2, j1, j2, size) {
  const rc11 = rowCol(i1, size);
  const rc12 = rowCol(i2, size);
  const rc21 = rowCol(j1, size);
  const rc22 = rowCol(j2, size);

  const x1 = rc11.col;
  const x2 = rc12.col;
  const x3 = rc21.col;
  const x4 = rc22.col;

  if (Math.min(x1, x2) >= Math.max(x3, x4) || Math.max(x1, x2) <= Math.min(x3, x4)) {
    return false;
  }

  const y1 = rc11.row;
  const y2 = rc12.row;
  const y3 = rc21.row;
  const y4 = rc22.row;

  if (Math.min(y1, y2) >= Math.max(y3, y4) || Math.max(y1, y2) <= Math.min(y3, y4)) {
    return false;
  }

  const dx21 = x2 - x1;
  const dy21 = y2 - y1;

  const dx43 = x4 - x3;
  const dy43 = y4 - y3;

  if ((dx21 === 0 && dx43 === 0) || (dy21 === 0 && dy43 === 0)) {
    return false;
  }

  const a12 = dy21 / dx21;
  const b12 = y1 - a12 * x1;

  const a34 = dy43 / dx43;
  const b34 = y3 - a34 * x3;

  let x, y;

  if (dx21 === 0) {
    x = x1;
    y = a34 * x + b34;
  } else if (dx43 === 0) {
    x = x3;
    y = a12 * x + b12;
  } else {
    x = (b34 - b12) / (a12 - a34);
    y = dy21 === 0 ? y1 : dy43 === 0 ? y3 : a12 * x + b12;
  }

  return (
    x >= Math.min(x1, x2) &&
    x <= Math.max(x1, x2) &&
    x >= Math.min(x3, x4) &&
    x <= Math.max(x3, x4) &&
    y >= Math.min(y1, y2) &&
    y <= Math.max(y1, y2) &&
    y >= Math.min(y3, y4) &&
    y <= Math.max(y3, y4)
  );
}

export function rowCol(i, size) {
  const row = Math.floor(i / size);
  const col = i % size;

  return { row, col };
}

export function canLink(from, to, props) {
  if (from === to) {
    return false;
  }

  const { dots, lines, size } = props;

  if (hidden(dots[from]) || hidden(dots[to])) {
    return false;
  }

  const rc1 = rowCol(from, size);
  const rc2 = rowCol(to, size);

  const dx = Math.abs(rc1.col - rc2.col);
  const dy = Math.abs(rc1.row - rc2.row);

  if ((dx === 0 && dy > 1) || (dy === 0 && dx > 1)) {
    return false; // line goes through another dot
  }

  let max = Math.max(dx, dy);
  let min = Math.min(dx, dy);

  if (dx > 1 && dy > 1 && (max % min === 0 || (max % 2 === 0 && min % 2 === 0) || (max % 3 === 0 && min % 3 === 0))) {
    return false; // line goes through another dot
  }

  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];

    if (sameLine(from, to, l.from, l.to)) {
      return false;
    }

    if (isNum(touch(from, to, l.from, l.to))) {
      continue;
    }

    if (cross(from, to, l.from, l.to, size)) {
      return false;
    }
  }

  return true;
}

export function onFromSelected(selected, props) {
  const { dots } = props;

  for (let i = 0; i < dots.length; i++) {
    if (i === selected) {
      continue;
    }

    const d = dots[i];

    if (hidden(d)) {
      continue;
    }

    d.visibility = canLink(selected, i, props) ? undefined : DISABLED;
  }
}

export function addLine(from, to, props, duration) {
  const { lines, size } = props;
  const l = { from, to };

  if (duration) {
    l.visibility = duration;
  }

  lines.push(l);

  const shapes = [];

  if (lines.length < 3) {
    return shapes;
  }

  for (let i = 0; i < lines.length; i++) {
    const l2 = lines[i];

    if (hidden(l2)) {
      continue;
    }

    let t = touch(from, to, l2.from, l2.to);

    if (!isNum(t)) {
      continue;
    }

    let t1 = t === from ? to : from;
    let t2 = t === l2.from ? l2.to : l2.from;

    for (let j = 0; j < lines.length; j++) {
      const l3 = lines[j];

      if (hidden(l3) || !sameLine(t1, t2, l3.from, l3.to)) {
        continue;
      }

      const v = _.sortBy([t, t1, t2]);

      let exists;

      for (let k = 0; k < shapes.length; k++) {
        const s = shapes[k];

        if (v[0] === s.i1 && v[1] === s.i2 && v[2] === s.i3) {
          exists = true;
          break;
        }
      }

      if (!exists) {
        const s = { i1: v[0], i2: v[1], i3: v[2], player: props.currentPlayer };
        s.area = shapeArea(s, size);
        shapes.push(s);
      }
    }
  }

  return shapes;
}

function addLineMakesShapes(from, to, props) {
  const { lines } = props;

  lines.push({ from, to });

  if (lines.length < 3) {
    return false;
  }

  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];

    if (hidden(l)) {
      continue;
    }

    let t = touch(from, to, l.from, l.to);

    if (!isNum(t)) {
      continue;
    }

    let t1 = t === from ? to : from;
    let t2 = t === l.from ? l.to : l.from;

    for (let j = 0; j < lines.length; j++) {
      const l2 = lines[j];

      if (hidden(l2) || !sameLine(t1, t2, l2.from, l2.to)) {
        continue;
      }

      return true;
    }
  }

  return false;
}

export function hidden(ob) {
  return ob && ob.visibility === HIDDEN;
}

function hide(ob) {
  ob.visibility = HIDDEN;
}

export function restoreVisibility(ob) {
  delete ob.visibility;
}

export function select(dot) {
  dot.visibility = SELECTED;
}

export function disabled(dot) {
  return dot && dot.visibility === DISABLED;
}

function covered(index, props) {
  const { shapes, size } = props;

  for (let i = 0; i < shapes.length; i++) {
    const s = shapes[i];

    if (s.area < 3) {
      continue;
    }

    const rc = rowCol(index, size);
    const rc1 = rowCol(s.i1, size);
    const rc2 = rowCol(s.i2, size);
    const rc3 = rowCol(s.i3, size);

    const fab = (rc.row - rc1.row) * (rc2.col - rc1.col) - (rc.col - rc1.col) * (rc2.row - rc1.row);
    const fbc = (rc.row - rc2.row) * (rc3.col - rc2.col) - (rc.col - rc2.col) * (rc3.row - rc2.row);
    const fca = (rc.row - rc3.row) * (rc1.col - rc3.col) - (rc.col - rc3.col) * (rc1.row - rc3.row);

    if (fab * fbc > 0 && fbc * fca > 0) {
      return true;
    }
  }

  return false;
}

function lineExists(from, to, props) {
  const { lines } = props;
  return _.find(lines, l => sameLine(l.from, l.to, from, to));
}

export function onShapesAdded(props) {
  const { dots, lines, shapes } = props;

  for (let i = 0; i < dots.length; i++) {
    if (hidden(dots[i])) {
      continue;
    }

    if (covered(i, props)) {
      hide(dots[i]);
      continue;
    }

    let _hide = true;

    for (let j = 0; j < dots.length; j++) {
      if (canLink(i, j, props)) {
        _hide = false;
        break;
      }
    }

    if (_hide) {
      hide(dots[i]);
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];

    if (!hidden(l) && (covered(l.from, props) || covered(l.to, props))) {
      hide(l);
      continue;
    }

    let _hide = true;

    for (let i = 0; i < dots.length; i++) {
      if (covered(i, props)) {
        continue;
      }

      const canLinkFrom = canLink(i, l.from, props);
      const existsFrom = lineExists(i, l.from, props);
      const canLinkTo = canLink(i, l.to, props);
      const existsTo = lineExists(i, l.to, props);

      if ((canLinkFrom && canLinkTo) || (canLinkFrom && existsTo) || (canLinkTo && existsFrom)) {
        _hide = false;
        break;
      }
    }

    if (_hide) {
      hide(l);
    }
  }

  for (let i = shapes.length - 1; i >= 0; i--) {
    const s = shapes[i];

    if (covered(s.i1, props) || covered(s.i2, props) || covered(s.i3, props)) {
      shapes.splice(i, 1);
    }
  }
}

export function shapeArea(shape, size) {
  const rc1 = rowCol(shape.i1, size);
  const rc2 = rowCol(shape.i2, size);
  const rc3 = rowCol(shape.i3, size);

  const a = Math.abs(rc1.col * (rc2.row - rc3.row) + rc2.col * (rc3.row - rc1.row) + rc3.col * (rc1.row - rc2.row));

  return a;
}

export function playerColor(player) {
  return player === PLAYER1 ? PLAYER1_COLOR : player === PLAYER2 ? PLAYER2_COLOR : null;
}

export function playerArea(player, props) {
  const shapes = _.filter(props.shapes, s => s.player === player);
  return _.sumBy(shapes, s => s.area);
}

export function totalArea(size) {
  return (size - 1) * (size - 1) * 2;
}

export function winnerInfo(props) {
  const area1 = playerArea(PLAYER1, props);
  const area2 = playerArea(PLAYER2, props);

  if (area1 + area2 < totalArea(props.size)) {
    return { area1 };
  }

  const winner = area1 > area2 ? PLAYER1 : area1 < area2 ? PLAYER2 : DRAW;
  let winnerMessage;
  let sound;

  if (winner === DRAW) {
    winnerMessage = "It's a draw!";
    sound = 'draw';
  } else if (props.players === HUMAN_VS_ROBOT) {
    winnerMessage = winner === PLAYER1 ? 'You won!' : 'You lost!';
    sound = winner === PLAYER1 ? 'won' : 'lost';
  } else {
    winnerMessage = winner === PLAYER1 ? 'Player 1 won!' : 'Player 2 won!';
    sound = winner === PLAYER1 ? 'player1wins' : 'player2wins';
  }

  return { winner, winnerMessage, area1, sound };
}

export function getMoreStats(stats, size) {
  const plays = stats.wins1 + stats.wins2 + stats.draws;
  const area = totalArea(size);
  const best = plays && stats.bestArea ? Math.round((stats.bestArea * 100) / area) : 0;
  const ave = plays ? Math.round((stats.area1 * 100) / (area * plays)) : 0;
  const points = 2 * stats.wins1 + 1.5 * stats.draws + 1 * stats.wins2;
  const grade = plays ? Math.round((Math.min(10, plays) * ave * (2 * points)) / (3 * plays)) : 0;

  return { plays, best, ave, grade };
}

export function robot(props) {
  return props.players === HUMAN_VS_ROBOT && props.currentPlayer === PLAYER2;
}

export function toJson(props) {
  const json = JSON.stringify(props, [
    'players',
    'skill',
    'size',
    'currentPlayer',
    'dots',
    'visibility',
    'lines',
    'from',
    'to',
    'shapes',
    'player',
    'i1',
    'i2',
    'i3',
    'area',
  ]);

  return json;
}

export function fromJson(json) {
  return JSON.parse(json);
}

export function roboMove(props, processClicks) {
  delay(() => {
    const link = findLink(props);

    if (link) {
      processClicks(link);
    }
  }, SLOW);
}

function findLink(props) {
  switch (props.skill) {
    case BEGINNER:
      const { link } = findLinkBeginner(props);
      return link;
    case JUNIOR:
      return findLinkJunior(props);
    case SENIOR:
      return findLinkSenior(props);
    case GRADUATE:
      return findLinkGraduate(props);
    default:
      break;
  }
}

function randomizeTos(i, count, off) {
  const tos = [];

  for (let j = i + 1; j < count; j++) {
    tos.push((j + off) % count);
  }

  return tos;
}

function randomTo(tos) {
  const i = _.random(tos.length - 1);
  const to = tos[i];

  _.pullAt(tos, [i]);

  return to;
}

function findLinkBeginner(props) {
  const { dots } = props;
  const count = dots.length;
  const json = toJson(props);
  const off = _.random(count - 1);
  let fromTo;

  for (let i = 0; i < count - 1; i++) {
    const from = (i + off) % count;

    if (hidden(dots[from])) {
      continue;
    }

    const tos = randomizeTos(i, count, off);

    while (tos.length) {
      const to = randomTo(tos);

      if (!canLink(from, to, props)) {
        continue;
      }

      const shapes = addLine(from, to, fromJson(json));

      if (shapes.length) {
        return { link: [from, to], shapes };
      }

      if (!fromTo) {
        fromTo = [from, to];
      }
    }
  }

  return { link: fromTo, shapes: [] };
}

function findLinkJunior(props) {
  const { dots } = props;
  const count = dots.length;
  const json = toJson(props);
  const links = [];
  const off = _.random(count - 1);

  for (let i = 0; i < count - 1; i++) {
    const from = (i + off) % count;

    if (hidden(dots[from])) {
      continue;
    }

    const tos = randomizeTos(i, count, off);

    while (tos.length) {
      const to = randomTo(tos);

      if (!canLink(from, to, props)) {
        continue;
      }

      if (addLineMakesShapes(from, to, fromJson(json))) {
        return [from, to];
      }

      links.push({ from, to });
    }
  }

  if (links.length) {
    for (let i = 0; i < links.length; i++) {
      const l = links[i];
      const props2 = fromJson(json);

      props2.lines.push(l);
      const { link, shapes } = findLinkBeginner(props2);

      if (link && !shapes.length) {
        return _.values(l);
      }
    }

    return _.values(links[0]);
  }

  return null;
}

function findLinkSenior(props) {
  const { dots } = props;
  const count = dots.length;
  const json = toJson(props);
  const links = [];
  const off = _.random(count - 1);

  for (let i = 0; i < count - 1; i++) {
    const from = (i + off) % count;

    if (hidden(dots[from])) {
      continue;
    }

    const tos = randomizeTos(i, count, off);

    while (tos.length) {
      const to = randomTo(tos);

      if (!canLink(from, to, props)) {
        continue;
      }

      if (addLineMakesShapes(from, to, fromJson(json))) {
        return [from, to];
      }

      links.push({ from, to });
    }
  }

  if (links.length) {
    const loss = { area: 999 };

    for (let i = 0; i < links.length; i++) {
      const l = links[i];
      const props2 = fromJson(json);

      props2.lines.push(l);
      const area = addLineRecursiveGetArea(props2, loss.area);

      if (area === 0) {
        return [l.from, l.to];
      }

      if (area < loss.area) {
        loss.area = area;
        loss.link = l;
      }
    }

    if (loss.link) {
      return [loss.link.from, loss.link.to];
    }

    return _.values(links[0]);
  }

  return null;
}

function addLineRecursiveGetArea(props, minarea) {
  let area = 0;

  while (true) {
    const { link, shapes } = findLinkBeginner(props);

    if (!link || !shapes.length) {
      break;
    }

    area += _.sumBy(shapes, s => s.area);

    if (minarea && area >= minarea) {
      break;
    }

    props.lines.push({ from: link[0], to: link[1] });
  }

  return area;
}

function addLineRecursiveGetShapes(props) {
  const allShapes = [];

  while (true) {
    const { link, shapes } = findLinkBeginner(props);

    if (!link || !shapes.length) {
      break;
    }

    allShapes.push(...shapes);

    if (_.sumBy(allShapes, s => s.area) > 4) {
      break;
    }

    props.lines.push({ from: link[0], to: link[1] });
  }

  return allShapes;
}

function findLinkGraduate(props) {
  const { dots } = props;
  const count = dots.length;
  const json = toJson(props);
  const links = [];
  const off = _.random(count - 1);

  for (let i = 0; i < count - 1; i++) {
    const from = (i + off) % count;

    if (hidden(dots[from])) {
      continue;
    }

    const tos = randomizeTos(i, count, off);

    while (tos.length) {
      const to = randomTo(tos);

      if (!canLink(from, to, props)) {
        continue;
      }

      let shapes = addLine(from, to, fromJson(json));

      if (shapes.length) {
        const props2 = fromJson(json);
        props2.lines.push({ from, to });

        const moreShapes = addLineRecursiveGetShapes(props2);
        shapes.push(...moreShapes);

        const area = _.sumBy(shapes, s => s.area);
        const lineDiff = props2.lines.length - props.lines.length;

        if (area === 1) {
          return [from, to];
        }

        if (area === 2) {
          const shared = sharedVertices(shapes);

          if (shared.length === 2 && sameLine(shared[0], shared[1], from, to)) {
            const link = _.last(props2.lines);
            const props3 = fromJson(json);

            props3.lines.push(link);
            const oppGain = addLineRecursiveGetArea(props3);

            const link2 = findLinkSenior(props3);

            if (link2) {
              props3.lines.push({ from: link2[0], to: link2[1] });
              const futureGain = addLineRecursiveGetArea(props3);

              if (futureGain > area && futureGain > oppGain) {
                return [link.from, link.to];
              }
            }
          }

          return [from, to];
        }

        if (area === 3 && lineDiff === 3) {
          const { lines } = props2;

          for (let k = 1; k <= 3; k++) {
            const l = lines[lines.length - k];

            const props3 = fromJson(json);
            props3.lines.push(l);

            if (!addLineMakesShapes(l.from, l.to, props3)) {
              continue;
            }

            let count = 0;

            for (let m = 0; m < shapes.length; m++) {
              if (isSideOf(l.from, l.to, shapes[m])) {
                ++count;
              }
            }

            if (count === 1) {
              return [l.from, l.to];
            }
          }
        }

        if (area === 4 && shapes.length === 4 && lineDiff === 3) {
          for (let i = 1; i <= 3; i++) {
            var link = props2.lines[props2.lines.length - i];
            const props3 = fromJson(json);

            if (!addLineMakesShapes(link.from, link.to, props3)) {
              const oppGain = addLineRecursiveGetArea(props3);

              const link2 = findLinkSenior(props3);

              if (link2) {
                props3.lines.push({ from: link2[0], to: link2[1] });
                const futureGain = addLineRecursiveGetArea(props3);

                if (futureGain > area && futureGain > oppGain) {
                  return [link.from, link.to];
                }
              }

              break;
            }
          }
        }

        return [from, to];
      }

      links.push({ from, to });
    }
  }

  if (links.length) {
    const loss = { area: 999 };

    for (let i = 0; i < links.length; i++) {
      const l = links[i];
      const props2 = fromJson(json);

      props2.lines.push(l);
      const area = addLineRecursiveGetArea(props2, loss.area);

      if (area === 0) {
        return [l.from, l.to];
      }

      if (area < loss.area) {
        loss.area = area;
        loss.link = l;
      }
    }

    if (loss.link) {
      if (loss.area === 2) {
        const props2 = fromJson(json);
        props2.lines.push(loss.link);
        const shapes = addLineRecursiveGetShapes(props2);
        const shared = sharedVertices(shapes);
        return [shared[0], shared[1]];
      }

      return [loss.link.from, loss.link.to];
    }

    return _.values(links[0]);
  }

  return null;
}

function sharedVertices(shapes) {
  let shared = [shapes[0].i1, shapes[0].i2, shapes[0].i3];

  for (let i = 1; i < shapes.length; i++) {
    const s = shapes[i];
    shared = _.intersection(shared, [s.i1, s.i2, s.i3]);
  }

  return shared;
}

function isSideOf(i1, i2, s) {
  return sameLine(i1, i2, s.i1, s.i2) || sameLine(i1, i2, s.i2, s.i3) || sameLine(i1, i2, s.i3, s.i1);
}

const baseKey = props => {
  const { players, skill, size } = props;
  return `TA ${players.slice(6)} • ${skill ? `${skill.slice(0, 1)}` : ''}${size} • `;
};

export function boardKey(props) {
  return `${baseKey(props)}BOARD`;
}

export function statsKey(props) {
  return `${baseKey(props)}STATS`;
}

export function getStats(props, resolve) {
  const key = statsKey(props);
  dbget(key, stats => {
    if (!stats) {
      stats = { wins1: 0, wins2: 0, draws: 0, area1: 0 };
    }

    resolve(stats);
  });
}

export function setStats(props, updateStats, surrender) {
  var { winner, area1 } = winnerInfo(props);

  if (surrender) {
    winner = PLAYER2;
  }

  if (!winner) {
    return;
  }

  getStats(props, stats => {
    switch (winner) {
      case PLAYER1:
        stats.wins1 += 1;
        break;
      case PLAYER2:
        stats.wins2 += 1;
        break;
      case DRAW:
        stats.draws += 1;
        break;
      default:
        break;
    }

    stats.area1 += area1;

    if (area1 > (stats.bestArea || 0)) {
      stats.bestArea = area1;
    }

    dbset(statsKey(props), stats);
    updateStats(stats);
  });
}

export function boardWidth(props) {
  const { x, y } = windowSize();
  const width = (Math.min(x, y - 157) * ((props.size - 2) * 0.3 + 7)) / 10;
  return Math.min(width, 800);
}

export function solo(props) {
  return props.players === HUMAN_VS_ROBOT;
}

export function gameStarted(props) {
  const { lines } = props;
  return lines && lines.length;
}

export function getHints(props) {
  const { dots } = props;
  const count = dots.length;
  const json = toJson(props);
  const hints = [];

  for (let i = 0; i < count - 1; i++) {
    if (hidden(dots[i])) {
      continue;
    }

    for (let j = i + 1; j < count; j++) {
      if (canLink(i, j, props) && addLineMakesShapes(i, j, fromJson(json))) {
        hints.push({ from: i, to: j });
      }
    }
  }

  return hints;
}

export function canSurrender(props) {
  return gameStarted(props) && (!robot(props) || winnerInfo(props).winner);
}

export function canPlayBack(props) {
  const p = props;
  return !p.playback && gameStarted(p) && (winnerInfo(p).winner || !robot(p));
}

export function skillIcon(props, skill) {
  if (!solo(props)) {
    return Player2Icon;
  }

  if (!skill) {
    skill = props.skill;
  }

  switch (skill) {
    case BEGINNER:
      return BeginnerIcon;
    case JUNIOR:
      return JuniorIcon;
    case SENIOR:
      return SeniorIcon;
    case GRADUATE:
      return GraduateIcon;
    default:
      break;
  }
}

export function sizeName(size) {
  switch (size) {
    case 3:
      return 'XS';
    case 4:
      return 'S';
    case 5:
      return 'M';
    case 6:
      return 'L';
    case 7:
      return 'XL';
    default:
      break;
  }
}

export function selectorClass(x, y) {
  return `triangula-page simple-selector${x < y ? '' : ' simple-selector-landscape'}`;
}
